import React, { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalculator,
  faCircleCheck,
  faArrowRight,
  faArrowTrendDown,
} from '@fortawesome/free-solid-svg-icons';
import CgRadixButton from 'modules/Common/ui/CgRadixButton';
import { CgFilterSelect } from 'modules/Common/ui/CgFilterDropdown';
import { useCalculateStore } from 'modules/Analytics/stores/useCalculateStore';
import { useBranding } from 'modules/App/hooks/useBranding';
import formatCnCode from 'modules/Common/utils/formatCnCode';
import { formatCurrency, formatNumber, toFixedNonZero } from 'helpers/numberFormat';
import Decimal from 'decimal.js';

const fmtCurrency = (v, dp = 0) => {
  if (v == null || !Number.isFinite(v)) return '–';
  return formatCurrency(v, {
    currency: 'EUR',
    minimumFractionDigits: dp,
    maximumFractionDigits: dp,
  });
};

const fmtEmissions = (v) => {
  if (v == null || !Number.isFinite(v)) return '–';
  return toFixedNonZero(new Decimal(v), 3);
};

const fmtTonnes = (v) => {
  if (!Number.isFinite(v)) return '0';
  // maximumFractionDigits: 3 matches Intl's own default, which this used
  // before it moved onto the shared convention-aware formatter.
  return formatNumber(v, { maximumFractionDigits: 3 });
};

/**
 * Standalone CBAM cost calculator section that lives below the Products table
 * on the public share page.
 *
 * Two jobs:
 *   1. Give a cost-curious visitor a concrete number tied to a specific product
 *      from the catalog, at their own annual volume.
 *   2. Convert that visitor into a Carbon Glance trial — the CTA at the bottom
 *      pitches the "do this for your whole supplier list" upgrade.
 *
 * If no products in the catalog have an EU default factor available the whole
 * section hides — the comparison stops being meaningful without a default.
 */
export default function CbamCostEstimator({ products = [], operatorName, onSignup }) {
  const { carbonPrice } = useCalculateStore();
  // Renders on the PUBLIC portal page, so the platform must be named by brand —
  // a whitelabel tenant's supplier pitches CG otherwise.
  const brand = useBranding();
  // The assumptions bar stores the price as a raw string while typing; coerce
  // to a number for the calculation and the "Assumes …" caption.
  const priceNum = Number(carbonPrice) || 0;

  // `useDirect` picks the emissions base on each side: direct-only when true,
  // otherwise the full total. The actual side subtracts the product's `sefa`,
  // the EU default side subtracts `defaultSefa` — both arrive from the backend.
  const emissionsBasis = (p) => (p?.useDirect ? p?.direct : p?.total);
  const defaultBasis = (p) => (p?.useDirect ? p?.defaultDirect : p?.defaultTotal);

  const calculatable = useMemo(
    () =>
      products.filter(
        (p) => Number.isFinite(emissionsBasis(p)) && Number.isFinite(defaultBasis(p)),
      ),
    [products],
  );

  const [selectedId, setSelectedId] = useState(() => {
    if (calculatable.length === 0) return null;
    const p = calculatable[0];
    return `${p.cnCode}|${p.productName}|${p.reportingPeriod}`;
  });
  const [volumeStr, setVolumeStr] = useState('500');

  // {value, label} options for CgFilterSelect — labels include CN code, year,
  // and product ID so the built-in typeahead matches whichever the user types.
  const productOptions = useMemo(
    () =>
      calculatable.map((p) => ({
        value: `${p.cnCode}|${p.productName}|${p.reportingPeriod}`,
        label: `${p.productName} · CN ${formatCnCode(p.cnCode)} (${p.reportingPeriod})`,
      })),
    [calculatable],
  );

  const selected = useMemo(() => {
    if (!selectedId) return null;
    return (
      calculatable.find(
        (p) => `${p.cnCode}|${p.productName}|${p.reportingPeriod}` === selectedId,
      ) || calculatable[0]
    );
  }, [calculatable, selectedId]);

  const volume = useMemo(() => {
    const n = parseFloat(volumeStr);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }, [volumeStr]);

  const calc = useMemo(() => {
    if (!selected) return null;
    const sefa = selected.sefa ?? 0;
    const defaultSefa = selected.defaultSefa ?? 0;
    const actualChargeable = Math.max(0, (emissionsBasis(selected) ?? 0) - sefa);
    const actualCostPerT = actualChargeable * priceNum;
    const defaultChargeable = Math.max(0, (defaultBasis(selected) ?? 0) - defaultSefa);
    const defaultCostPerT = defaultChargeable * priceNum;
    const actualPerYr = actualCostPerT * volume;
    const defaultPerYr = defaultCostPerT * volume;
    const savingsPerYr = defaultPerYr - actualPerYr;
    const savingsPct = defaultPerYr > 0 ? (savingsPerYr / defaultPerYr) * 100 : null;
    return {
      sefa,
      actualCostPerT,
      defaultCostPerT,
      actualPerYr,
      defaultPerYr,
      savingsPerYr,
      savingsPct,
    };
  }, [selected, volume, priceNum]);

  if (calculatable.length === 0) return null;

  const handleSignup = () => {
    if (typeof onSignup === 'function') {
      onSignup();
      return;
    }
    window.open('/sign-up?intent=multi-supplier-catalog', '_blank', 'noopener');
  };

  return (
    <div className="mx-4 mt-4 mb-4 rounded-xl bg-white border border-slate-200 shadow-sm overflow-hidden">
      <div
        className="h-1 w-full"
        style={{ backgroundColor: 'rgb(var(--primary-color))' }}
      />

      <div className="px-5 py-5 sm:px-6">
        {/* Header */}
        <div className="flex items-start gap-3 mb-3">
          <div
            className="w-8 h-8 rounded-md flex items-center justify-center shrink-0 mt-0.5"
            style={{
              backgroundColor: 'rgb(var(--primary-color) / 0.1)',
              color: 'rgb(var(--primary-color))',
            }}
          >
            <FontAwesomeIcon
              icon={faCalculator}
              className="text-sm"
            />
          </div>
          <div className="min-w-0">
            <h3 className="text-base sm:text-lg font-bold leading-tight text-slate-900">
              Estimate your CBAM cost
            </h3>
            <p className="text-xs text-slate-600 mt-0.5 leading-snug">
              See what sourcing from {operatorName || 'this supplier'} would actually cost you —
              versus using EU default factors.
            </p>
            <p className="text-[11px] text-slate-400 mt-1">
              Assumes a certificate price of{' '}
              <span className="font-semibold text-slate-600">€{priceNum}/tCO₂e</span>.
            </p>
          </div>
        </div>

        {/* Inputs — standard label-above-input pattern with white backgrounds
            so they read as obviously editable. Product is a searchable
            CgFilterSelect so users can type a CN code, year, or SKU to find
            the right row. */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
          <div className="sm:col-span-2 min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
              Product
            </div>
            <CgFilterSelect
              options={productOptions}
              value={selectedId || ''}
              onChange={(v) => v && setSelectedId(v)}
              placeholder="Select a product"
              searchable
              isClearable={false}
            />
          </div>

          <div className="min-w-0">
            <label
              htmlFor="estimator-volume"
              className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1"
            >
              Annual volume (t)
            </label>
            <input
              id="estimator-volume"
              type="number"
              min="0"
              step="50"
              inputMode="numeric"
              value={volumeStr}
              onChange={(e) => setVolumeStr(e.target.value)}
              className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2 bg-white text-slate-700 font-medium
                         focus:outline-none focus:ring-2 focus:ring-primaryColor/30 transition-colors
                         [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            />
          </div>
        </div>

        {/* Comparison */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <div
            className="rounded-lg p-3.5 border"
            style={{
              backgroundColor: 'rgb(var(--primary-color) / 0.04)',
              borderColor: 'rgb(var(--primary-color) / 0.25)',
            }}
          >
            <div className="flex items-center gap-1.5 mb-1">
              <FontAwesomeIcon
                icon={faCircleCheck}
                style={{ color: 'rgb(var(--primary-color))' }}
                className="text-xs"
              />
              <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-700">
                With this data
              </span>
            </div>
            <div className="text-xl font-bold text-slate-900 leading-tight">
              {fmtCurrency(calc?.actualPerYr)}
              <span className="text-xs font-normal text-slate-500"> /yr</span>
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              {fmtCurrency(calc?.actualCostPerT, 2)}/t · {selected?.useDirect ? 'Direct' : 'SEE'}{' '}
              {fmtEmissions(emissionsBasis(selected))} tCO₂e/t
            </div>
          </div>

          <div className="rounded-lg p-3.5 border border-slate-200 bg-slate-50">
            <div className="flex items-center gap-1.5 mb-1">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                EU default factor
              </span>
            </div>
            <div className="text-xl font-bold text-slate-700 leading-tight">
              {fmtCurrency(calc?.defaultPerYr)}
              <span className="text-xs font-normal text-slate-500"> /yr</span>
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              {fmtCurrency(calc?.defaultCostPerT, 2)}/t · {selected?.useDirect ? 'Direct' : 'SEE'}{' '}
              {fmtEmissions(defaultBasis(selected))} tCO₂e/t
            </div>
          </div>
        </div>

        {/* Savings */}
        {calc && calc.savingsPerYr > 0 ? (
          <div
            className="rounded-lg px-4 py-3 mb-3 flex items-baseline gap-3 flex-wrap"
            style={{
              backgroundColor: 'rgb(var(--primary-color) / 0.08)',
              border: '1px solid rgb(var(--primary-color) / 0.2)',
            }}
          >
            <FontAwesomeIcon
              icon={faArrowTrendDown}
              style={{ color: 'rgb(var(--primary-color-dark))' }}
              className="text-sm"
            />
            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">
              Estimated saving
            </span>
            <span
              className="text-xl font-bold"
              style={{ color: 'rgb(var(--primary-color-dark))' }}
            >
              {fmtCurrency(calc.savingsPerYr)}
              <span className="text-xs font-normal text-slate-500"> /yr</span>
            </span>
            {calc.savingsPct != null && (
              <span className="text-xs font-semibold text-slate-600">
                {calc.savingsPct.toFixed(1)}% vs. default
              </span>
            )}
            <span className="text-[11px] text-slate-500 ml-auto">on {fmtTonnes(volume)} t/yr</span>
          </div>
        ) : (
          calc && (
            <div className="rounded-lg px-4 py-3 mb-3 bg-slate-50 border border-slate-200 text-xs text-slate-600">
              This product’s actual emissions are at or above the EU default factor — still useful
              for accurate reporting, but no premium saving at this volume.
            </div>
          )
        )}

        {/* CTA footer */}
        <div className="border-t border-slate-100 pt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-slate-600 leading-snug max-w-xl">
            Sourcing from <span className="font-semibold text-slate-800">many</span> suppliers?{' '}
            {brand.name} forecasts your full CBAM bill, applies defaults to fill data gaps, and
            surfaces savings like these automatically.
          </p>
          <CgRadixButton
            size="md"
            variant="primary"
            onClick={handleSignup}
            className="whitespace-nowrap shrink-0"
          >
            Start free trial
            <FontAwesomeIcon
              icon={faArrowRight}
              className="text-xs"
            />
          </CgRadixButton>
        </div>
      </div>
    </div>
  );
}
